import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { StepperControlsComponent } from '../../../../../components/ui/stepper-controls/stepper-controls.component';
import { ProgressCircleComponent } from '../../../../../components/progress-circle/progress-circle.component';
import { CsvCleanupTableComponent } from '../../../../../components/tables/csv-cleanup-table/csv-cleanup-table.component';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import {
  CsvServiceService,
  IBatchUploadProgress,
} from '../../../../../services/csv/csv-service.service';
import { cn, getCountryCode } from '../../../../../../utils/utils';
import { countries } from '../../../../../../utils/countriesList';
import { formatCount } from '../../../../../../utils/sms';
import { csvFileName, downloadCsv } from '../../../../../../utils/csv';

@Component({
  selector: 'csv-clean-up',
  standalone: true,
  imports: [StepperControlsComponent, ProgressCircleComponent, CsvCleanupTableComponent, PromptCardComponent],
  templateUrl: './clean-up.component.html',
  styleUrls: ['../csv-step.css', './clean-up.component.css'],
})
export class CleanUpComponent implements OnInit, OnDestroy {
  router = inject(Router);
  csvService = inject(CsvServiceService);
  merge = cn;
  readonly formatCount = formatCount;
  readonly countries = countries;

  @ViewChild(CsvCleanupTableComponent) table?: CsvCleanupTableComponent;

  batchProgress: IBatchUploadProgress = {
    isBatching: false,
    currentBatch: 0,
    totalBatches: 0,
    percentage: 0,
  };
  private batchProgressSub?: Subscription;

  /** Country used to read numbers that have no country code. */
  defaultCountry = '';
  fixNotice = '';
  checkError = '';
  showInvalidPrompt = false;
  showRemoveRowsPrompt = false;

  // Counts, recomputed only when the rows change.
  private _version = -1;
  private _total = 0;
  private _invalid = 0;
  private _duplicates = 0;
  private _missingRegion = 0;

  private recount() {
    const version = this.csvService.version;
    const rows = this.csvService.getValidatedContacts;
    if (version === this._version && rows.length === this._total) return;
    let invalid = 0;
    let duplicates = 0;
    for (const row of rows) {
      if (row.batchLineValidationStatus !== 'PROCESSING_ERROR') continue;
      invalid++;
      if ((row.constraintViolations ?? []).some((v) => (v.message ?? '').startsWith('Same number as'))) {
        duplicates++;
      }
    }
    this._total = rows.length;
    this._invalid = invalid;
    this._duplicates = duplicates;
    this._missingRegion = this.csvService.rowsMissingRegion;
    this._version = version;
  }

  get totalCount(): number {
    this.recount();
    return this._total;
  }

  get invalidCount(): number {
    this.recount();
    return this._invalid;
  }

  get readyCount(): number {
    this.recount();
    return this._total - this._invalid;
  }

  get duplicateCount(): number {
    this.recount();
    return this._duplicates;
  }

  get missingRegionCount(): number {
    this.recount();
    return this._missingRegion;
  }

  get hasInvalidContacts(): boolean {
    return this.invalidCount > 0;
  }

  ngOnInit() {
    this.batchProgressSub = this.csvService.batchUploadProgress$.subscribe(
      (progress) => (this.batchProgress = progress)
    );
  }

  ngOnDestroy() {
    this.batchProgressSub?.unsubscribe();
  }

  onRowsChanged() {
    this.fixNotice = '';
  }

  applyDefaultCountry() {
    const code = this.defaultCountry ? getCountryCode(this.defaultCountry)?.countryCode : '';
    if (!code) return;
    const changed = this.csvService.applyDefaultRegion(code);
    this.table?.refresh();
    this.fixNotice = changed
      ? `Read ${formatCount(changed)} ${changed === 1 ? 'number' : 'numbers'} as ${this.defaultCountry} numbers.`
      : 'No rows needed a country code.';
  }

  /** Bulk remove asks first, like removing one row or the selected rows. */
  openRemoveRowsPrompt() {
    if (this.invalidCount) this.showRemoveRowsPrompt = true;
  }

  closeRemoveRowsPrompt() {
    this.showRemoveRowsPrompt = false;
  }

  confirmRemoveRowsNeedingFixes() {
    this.showRemoveRowsPrompt = false;
    const removed = this.csvService.removeRowsNeedingFixes();
    this.table?.refresh();
    this.fixNotice = removed
      ? `Removed ${formatCount(removed)} ${removed === 1 ? 'row' : 'rows'} from this import.`
      : '';
  }

  downloadRowsNeedingFixes() {
    const rows = this.csvService.getValidatedContacts.filter(
      (row) => row.batchLineValidationStatus === 'PROCESSING_ERROR'
    );
    if (!rows.length) return;
    downloadCsv(csvFileName('contacts', 'to fix'), this.csvService.problemRowsCsv(rows));
  }

  goToPrevious() {
    this.router.navigate(['organization', 'contacts', 'csv'], {
      queryParams: { title: 'mapping' },
    });
  }

  /** Continue: warn first when rows still need fixing. */
  requestNext() {
    if (!this.totalCount) return;
    if (this.hasInvalidContacts) {
      this.showInvalidPrompt = true;
      return;
    }
    this.goToNext();
  }

  closeInvalidPrompt() {
    this.showInvalidPrompt = false;
  }

  removeInvalidAndContinue() {
    this.showInvalidPrompt = false;
    this.csvService.removeRowsNeedingFixes();
    this.table?.refresh();
    if (this.totalCount) this.goToNext();
  }

  continueAnyway() {
    this.showInvalidPrompt = false;
    this.goToNext();
  }

  goToNext() {
    this.checkError = '';
    // Pull the personEntity objects out — the backend validation endpoint
    // expects raw contacts, not the wrapped batch-line shape.
    const contacts = this.csvService.getValidatedContacts.map((line) => line.personEntity);
    this.csvService.validateContacts(contacts as any).subscribe({
      next: () => {
        this.router.navigate(['organization', 'contacts', 'csv'], {
          queryParams: { title: 'review' },
        });
      },
      error: () => {
        this.checkError =
          'The rows could not be checked with the server. Check your connection, then select Continue again.';
      },
    });
  }
}
